import type { CalcData } from '../components/HousePreview3D';

export interface SavedQuote {
  id: string;
  userId: string;
  createdAt: string;
  name: string;
  calcData: CalcData;
}

const STORAGE_KEY = 'gevel_saved_quotes';

function getAll(): SavedQuote[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function persist(quotes: SavedQuote[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(quotes));
}

export function getQuotesForUser(userId: string): SavedQuote[] {
  return getAll().filter(q => q.userId === userId).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

export function saveQuote(userId: string, calcData: CalcData, name?: string): SavedQuote {
  const all = getAll();
  const quote: SavedQuote = {
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    userId,
    createdAt: new Date().toISOString(),
    name: name || `${calcData.productName} — ${calcData.area} m²`,
    calcData,
  };
  all.push(quote);
  persist(all);
  return quote;
}

export function deleteQuote(quoteId: string): void {
  const all = getAll().filter(q => q.id !== quoteId);
  persist(all);
}

export function generateQuotePDF(quote: SavedQuote) {
  const d = quote.calcData;
  const date = new Date(quote.createdAt).toLocaleDateString('nl-BE', { year: 'numeric', month: 'long', day: 'numeric' });

  const html = `<!DOCTYPE html>
<html lang="nl">
<head>
<meta charset="UTF-8">
<title>Offerte — ${d.productName}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #1a1a1a; padding: 40px; max-width: 800px; margin: 0 auto; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #c47a3a; padding-bottom: 20px; margin-bottom: 30px; }
  .logo { font-size: 24px; font-weight: bold; color: #1a1a1a; }
  .logo span { color: #c47a3a; }
  .meta { text-align: right; font-size: 13px; color: #666; }
  .meta strong { display: block; font-size: 16px; color: #1a1a1a; margin-bottom: 4px; }
  h2 { font-size: 18px; color: #c47a3a; margin: 24px 0 12px; padding-bottom: 6px; border-bottom: 1px solid #eee; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; }
  th, td { padding: 10px 12px; text-align: left; font-size: 14px; }
  th { background: #faf7f3; color: #8a7e74; font-weight: 600; font-size: 12px; text-transform: uppercase; }
  td { border-bottom: 1px solid #f0ede8; }
  .amount { text-align: right; font-weight: 700; color: #c47a3a; }
  .total-row td { border-top: 2px solid #c47a3a; font-size: 16px; font-weight: 700; }
  .total-row .amount { font-size: 20px; }
  .vat-row td { color: #8a7e74; font-size: 13px; }
  .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #8a7e74; text-align: center; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; margin: 12px 0; }
  .info-item { font-size: 14px; }
  .info-label { color: #8a7e74; font-size: 12px; }
  @media print { body { padding: 20px; } }
</style>
</head>
<body>
  <div class="header">
    <div>
      <div class="logo">Gevelsteen<span>bedrijf</span>.be</div>
      <p style="font-size:12px;color:#8a7e74;margin-top:4px;">Premium Baksteenstrips — Direct van Fabrikant</p>
    </div>
    <div class="meta">
      <strong>Offerte</strong>
      Ref: ${quote.id.slice(0, 12).toUpperCase()}<br>
      Datum: ${date}
    </div>
  </div>

  <h2>Product</h2>
  <div class="info-grid">
    <div class="info-item"><span class="info-label">Productnaam</span><br><strong>${d.productName}</strong></div>
    <div class="info-item"><span class="info-label">Kleur / Textuur</span><br><strong>${d.productColor} — ${d.productTexture}</strong></div>
    <div class="info-item"><span class="info-label">Prijs per m²</span><br><strong>€${d.pricePerM2.toFixed(2)}</strong></div>
    <div class="info-item"><span class="info-label">Oppervlakte</span><br><strong>${d.area} m²</strong></div>
  </div>

  <h2>Parameters</h2>
  <div class="info-grid">
    <div class="info-item"><span class="info-label">Voegbreedte</span><br>${d.jointWidth} mm</div>
    <div class="info-item"><span class="info-label">Hoeken</span><br>${d.cornerCount} × ${d.cornerLength} m</div>
    <div class="info-item"><span class="info-label">Snijverlies</span><br>${d.cuttingLoss}%</div>
    <div class="info-item"><span class="info-label">Voegkleur</span><br>${d.groutColor}</div>
  </div>

  <h2>Materiaaloverzicht</h2>
  <table>
    <thead><tr><th>Artikel</th><th>Hoeveelheid</th><th style="text-align:right">Bedrag</th></tr></thead>
    <tbody>
      <tr>
        <td>Baksteenstrips — ${d.productName}</td>
        <td>${d.totalStones} stuks (${d.stonesPerM2}/m²)</td>
        <td class="amount">€${Math.round(d.stonePrice).toLocaleString('nl-BE')}</td>
      </tr>
      ${d.totalCornerPieces > 0 ? `<tr>
        <td>Hoekstenen</td>
        <td>${d.totalCornerPieces} stuks</td>
        <td class="amount">€${Math.round(d.cornerPrice).toLocaleString('nl-BE')}</td>
      </tr>` : ''}
      ${d.includeGlue ? `<tr>
        <td>Lijm</td>
        <td>${d.glueKg} kg (${d.glueBags} zakken à 25kg)</td>
        <td class="amount">€${d.gluePrice.toLocaleString('nl-BE')}</td>
      </tr>` : ''}
      ${d.includeGrout ? `<tr>
        <td>Voegsel (${d.groutColor})</td>
        <td>${d.groutKg} kg (${d.groutBags} zakken à 25kg)</td>
        <td class="amount">€${d.groutPrice.toLocaleString('nl-BE')}</td>
      </tr>` : ''}
      <tr class="total-row">
        <td colspan="2">Totaal (excl. BTW)</td>
        <td class="amount">€${Math.round(d.totalPrice).toLocaleString('nl-BE')}</td>
      </tr>
      <tr class="vat-row">
        <td colspan="2">Incl. 21% BTW</td>
        <td style="text-align:right">€${Math.round(d.totalPrice * 1.21).toLocaleString('nl-BE')}</td>
      </tr>
    </tbody>
  </table>

  <div class="footer">
    <p>Gevelsteenbedrijf.be — Antwerpen, België</p>
    <p style="margin-top:4px">* Richtprijzen. Exacte prijs in uw persoonlijke offerte. Geldig tot 30 dagen na datum.</p>
  </div>

  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}
